/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuthForm } from "./hooks";
import { ReactComponent as AirspanLogo } from "../../icons/airspan_stacked_lockup_blue.svg";
import { isError } from "../../utils/validations";
import alertIcon from "../../icons/alerticon.png";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import React from "react";
import '../../theme/designSystem.css';
import { Box, Dialog, DialogContent, Typography, Link } from "@mui/material";

export const AuthForm: React.FC = () => {
  const {
    submitHandler,
    setUsername,
    setPassword,
    username,
    password,
    errMsg,
    setErrMsg,
    isLoading,
    onBlurHandler,
    isSsoEnabled,
    shouldRedirectToSso,
    shouldHoldLoginUi,
    statusData,
    navigate,
  } = useAuthForm();

  const [showPassword, setShowPassword] = React.useState(false);

  const onChangeHandler = (e: any) => {
    const { name, value } = e.target;
    if (name === "username") {
      setUsername(value);
    } else if (name === "password") {
      setPassword(value);
    }
    setErrMsg({ ...errMsg, [name]: "", invalid: "", expired: "" });
  }

  const enableLoginButton = !!username && !!password && !isError(errMsg);

  if (shouldRedirectToSso === null) {
    return (
      <div className="flex justify-center items-center bg-primary min-h-screen p-4 overflow-auto">
        <div className="bg-background rounded-lg shadow-sm p-6 pt-3 w-full" style={{ maxWidth: '360px' }}>
          <div className="flex flex-col gap-3 justify-center items-center w-full">
            <AirspanLogo style={{ width: 220, height: 80 }} />
            <h1 className="font-black tracking-tight m-0 text-primary dark:text-primary-foreground" style={{ fontSize: '2.3rem', fontWeight: 900 }}>AMS</h1>
            <p className="text-sm text-muted-foreground m-0">Checking authentication...</p>
            <Spinner size="sm" className="text-primary" />
          </div>
        </div>
      </div>
    );
  }

  const handleClickShowPassword = () => {
    // setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Standby mode view
  if (statusData?.ha_state?.startsWith("HA_S") || statusData?.ha_state?.startsWith("HA_F")) {
    return (
      <Box
        className="login-page-backdrop"
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          backgroundColor: "var(--primary)",
        }}
      >
        <Dialog open maxWidth="sm" fullWidth>
          <DialogContent
            sx={{
              backgroundColor: "var(--card)",
              borderRadius: "8px",
              gap: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Box sx={{ mb: 3 }}>
              <img src={alertIcon} alt="Alert" />
            </Box>
            <Typography variant="h6" sx={{ color: "var(--foreground)", fontWeight: 600 }}>
              This server is in standby mode.
            </Typography>
            <Typography variant="body2" sx={{ color: "var(--muted-foreground)", mt: 1 }}>
              Please use the active server:
            </Typography>
            <Link
              href={`https://${statusData?.active_server_address}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mt: 1, fontWeight: 500, color: "var(--link)" }}
            >
              Server Link
            </Link>
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  if (shouldRedirectToSso) {
    return (
      <div className="flex justify-center items-center bg-primary min-h-screen p-4 overflow-auto">
        <div className="bg-background rounded-lg shadow-sm p-6 pt-3 w-full" style={{ maxWidth: '360px' }}>
          <div className="flex flex-col gap-3 justify-center items-center w-full">
            <AirspanLogo style={{ width: 220, height: 80 }} />
            <h1 className="font-black tracking-tight m-0 text-primary dark:text-primary-foreground" style={{ fontSize: '2.3rem', fontWeight: 900 }}>AMS</h1>
            <p className="text-sm text-muted-foreground m-0">Redirecting to SSO...</p>
            <Spinner size="sm" className="text-primary" />
          </div>
        </div>
      </div>
    );
  }

  if (!shouldHoldLoginUi) {
    return null;
  }

  return (
    <div className="flex justify-center items-center bg-primary min-h-screen p-4 overflow-auto">
      <form onSubmit={submitHandler} className="w-full" style={{ maxWidth: '360px' }}>
        <div className="bg-background rounded-lg shadow-sm p-6 pt-3">
          <div className="flex flex-col gap-0 justify-center items-center w-full">
            <div>
              <AirspanLogo style={{ width: 220, height: 80 }} />
            </div>
            <div className="flex flex-col items-center gap-0 mb-2">
              <h1 className="font-black tracking-tight m-0 text-primary dark:text-primary-foreground" style={{ fontSize: '2.3rem', fontWeight: 900 }}>AMS</h1>
              <p className="text-sm text-muted-foreground m-0 mt-[-2px]">Airspan Management System</p>
            </div>
            {!!errMsg?.invalid && (
              <div className="w-full rounded-md p-3 mt-4 mb-0 bg-destructive text-primary-foreground relative">
                <span className="font-normal">{errMsg?.invalid}</span>
                <button onClick={() => setErrMsg({ ...errMsg, invalid: "" })} className="absolute top-1 right-1 bg-destructive border-none text-inherit cursor-pointer text-lg leading-none p-1 hover:opacity-80">×</button>
              </div>
            )}
            {!!errMsg?.expired && (
              <div className="w-full rounded-md p-3 mt-4 mb-0 bg-warning text-primary-foreground relative">
                <span className="font-normal">{errMsg?.expired}</span>
                <button onClick={() => setErrMsg({ ...errMsg, expired: "" })} className="absolute top-1 right-1 bg-warning border-none text-inherit cursor-pointer text-lg leading-none p-1 hover:opacity-80">×</button>
                <a href="#" onClick={() => navigate("/password")} className="text-primary underline cursor-pointer mt-2 inline-block text-sm font-semibold">Please reset your password</a>
              </div>
            )}

            {statusData?.ha_state?.startsWith("HA_S") || statusData?.ha_state?.startsWith("HA_F") ? (
              <div className="p-4 rounded-lg bg-secondary w-full flex flex-col justify-center items-center">
                <div className="mb-3 flex justify-center items-center">
                  <img src={alertIcon} alt="Alert" style={{ width: 48, height: 48 }} />
                </div>
                <h3 className="text-foreground font-semibold mb-3 text-lg">
                  This server is in standby mode.
                </h3>
                <p className="text-foreground m-0 text-sm">
                  Please use the active server:{' '}
                  <a href={`https://${statusData?.active_server_address}`} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold no-underline">Server Link</a>
                </p>
              </div>
            ) : (
              <>
                <div className="w-full mt-3 space-y-2">
                  <Label htmlFor="username" className="text-base font-semibold text-foreground">
                    Username
                  </Label>
                  <Input
                    id="username"
                    autoFocus
                    name="username"
                    value={username}
                    onChange={onChangeHandler}
                    onBlur={onBlurHandler}
                    autoComplete="off"
                    placeholder="Username"
                  />
                  {errMsg?.username && (
                    <p className="text-xs font-sans text-destructive">
                      {errMsg?.username}
                    </p>
                  )}
                </div>
                <div className="w-full mt-4 space-y-2">
                  <Label htmlFor="password" className="text-base font-semibold text-foreground">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Password"
                      value={password}
                      onChange={onChangeHandler}
                      onBlur={onBlurHandler}
                      autoComplete="current-password"
                      style={{ paddingRight: '40px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-transparent border-none cursor-pointer"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 16 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 16 }} />
                      )}
                    </button>
                  </div>
                  {errMsg?.password && (
                    <p className="text-xs font-sans text-destructive">
                      {errMsg?.password}
                    </p>
                  )}
                </div>
                <div className="w-full mt-4">
                  <Button
                    variant="default"
                    className="w-full h-10 text-base font-normal"
                    disabled={isLoading || !enableLoginButton}
                    type="submit"
                  >
                    <div className="flex items-center justify-center gap-2">
                      {isLoading && <Spinner size="sm" className="text-primary-foreground" />}
                      {isLoading ? "Sending Request..." : "Log in"}
                    </div>
                  </Button>
                </div>
              </>
            )}
            {isSsoEnabled && ["HA_AF", "HA_A", "SA"].includes(statusData?.ha_state ?? "") && (
              <>
                <div className="w-full mt-4 flex justify-center items-center">
                  <span className="text-xs font-semibold text-muted-foreground">OR</span>
                </div>
                <div className="w-full mt-4">
                  <Button
                    variant="outline"
                    className="w-full h-10 text-base font-normal"
                    asChild
                    disabled={isLoading}
                  >
                    <a href="/v1/login/saml2/sso" className="no-underline" style={{ textTransform: 'none' }}>Log in with SSO</a>
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};